import { getApiOrigin } from '@/lib/api';
import { useAuthStore } from '@/stores/authStore';

export interface RiqueConversation {
  id: string;
  title: string;
  companyId?: string | null;
  createdAt: string;
  updatedAt: string;
  company?: { name: string } | null;
  _count?: { messages: number };
}

export interface RiqueMessage {
  id: string;
  role: 'USER' | 'ASSISTANT';
  content: string;
  createdAt: string;
  metadata?: {
    visualizations?: import('@/components/Rique/RiqueRichContent').RiqueVisualization[];
    deepLinks?: import('@/components/Rique/RiqueRichContent').RiqueDeepLink[];
    actionDraft?: import('@/components/Rique/RiqueRichContent').RiqueActionDraft;
  } | null;
}

function authHeaders(json = true): HeadersInit {
  const token = useAuthStore.getState().accessToken;
  const headers: HeadersInit = {};
  if (token) headers.Authorization = `Bearer ${token}`;
  if (json) headers['Content-Type'] = 'application/json';
  return headers;
}

export async function fetchRiqueStatus() {
  const res = await fetch(`${getApiOrigin()}/api/v1/rique/status`, { headers: authHeaders() });
  if (!res.ok) throw new Error('Falha ao verificar status do Rique.Ia');
  const body = await res.json();
  return body.data as { enabled: boolean; openAiConfigured: boolean; model: string };
}

export async function fetchRiqueConversations(): Promise<RiqueConversation[]> {
  const res = await fetch(`${getApiOrigin()}/api/v1/rique/conversations`, { headers: authHeaders() });
  if (!res.ok) throw new Error('Falha ao carregar conversas');
  const body = await res.json();
  return body.data as RiqueConversation[];
}

export async function createRiqueConversation(companyId?: string, title?: string): Promise<RiqueConversation> {
  const res = await fetch(`${getApiOrigin()}/api/v1/rique/conversations`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ companyId, title }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || 'Falha ao criar conversa');
  }
  const body = await res.json();
  return body.data as RiqueConversation;
}

export async function fetchRiqueMessages(conversationId: string): Promise<RiqueMessage[]> {
  const res = await fetch(`${getApiOrigin()}/api/v1/rique/conversations/${conversationId}/messages`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error('Falha ao carregar mensagens');
  const body = await res.json();
  return body.data as RiqueMessage[];
}

export async function sendRiqueMessage(
  conversationId: string,
  content: string,
  onToken?: (chunk: string) => void,
  pageContext?: string
): Promise<{ messageId: string; content: string; metadata?: RiqueMessage['metadata'] }> {
  const url = `${getApiOrigin()}/api/v1/rique/conversations/${conversationId}/messages?stream=true`;
  const res = await fetch(url, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ content, pageContext }),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || 'Falha ao enviar mensagem');
  }

  const contentType = res.headers.get('content-type') ?? '';
  if (!contentType.includes('text/event-stream') || !res.body) {
    const body = await res.json();
    return { messageId: body.data.assistantMessageId, content: body.data.content };
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let full = '';
  let messageId = '';
  let metadata: RiqueMessage['metadata'];

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() ?? '';
    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed.startsWith('data:')) continue;
      try {
        const parsed = JSON.parse(trimmed.slice(5).trim()) as {
          type: string;
          content?: string;
          messageId?: string;
          metadata?: RiqueMessage['metadata'];
        };
        if (parsed.type === 'token' && parsed.content) {
          full += parsed.content;
          onToken?.(parsed.content);
        }
        if (parsed.type === 'done' && parsed.messageId) {
          messageId = parsed.messageId;
          metadata = parsed.metadata ?? undefined;
        }
      } catch {
        // ignore
      }
    }
  }

  return { messageId, content: full, metadata };
}

export interface RiqueBriefing {
  geradoEm: string;
  sections: Array<{ titulo: string; itens: string[] }>;
}

export interface RiqueAlert {
  id: string;
  severidade: 'info' | 'warning' | 'critical';
  titulo: string;
  mensagem: string;
  createdAt: string;
}

export async function fetchRiqueBriefing(companyId?: string) {
  const qs = companyId ? `?companyId=${encodeURIComponent(companyId)}` : '';
  const res = await fetch(`${getApiOrigin()}/api/v1/rique/briefing${qs}`, { headers: authHeaders() });
  if (!res.ok) throw new Error('Falha ao carregar briefing');
  const body = await res.json();
  return body.data as { live: RiqueBriefing; cached: RiqueBriefing | null };
}

export async function fetchRiqueAlerts(companyId?: string) {
  const qs = companyId ? `?companyId=${encodeURIComponent(companyId)}` : '';
  const res = await fetch(`${getApiOrigin()}/api/v1/rique/alerts${qs}`, { headers: authHeaders() });
  if (!res.ok) throw new Error('Falha ao carregar alertas');
  const body = await res.json();
  return body.data as { live: RiqueAlert[]; cached: RiqueAlert[] };
}

export async function deleteRiqueConversation(conversationId: string): Promise<void> {
  const res = await fetch(`${getApiOrigin()}/api/v1/rique/conversations/${conversationId}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || 'Falha ao excluir conversa');
  }
}

export async function deleteAllRiqueConversations(): Promise<{ deletedCount: number }> {
  const res = await fetch(`${getApiOrigin()}/api/v1/rique/conversations`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || 'Falha ao limpar histórico');
  }
  const body = await res.json();
  return body.data as { deletedCount: number };
}

export async function sendRiqueConversationEmail(conversationId: string, to: string, companyId?: string) {
  const res = await fetch(`${getApiOrigin()}/api/v1/rique/conversations/${conversationId}/email`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ to, companyId }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || 'Falha ao enviar e-mail');
  }
  const body = await res.json();
  return body.data as { sentTo: string; messageCount: number };
}

export async function transcribeRiqueAudio(blob: Blob, filename = 'audio.webm'): Promise<string> {
  const form = new FormData();
  form.append('audio', blob, filename);
  const res = await fetch(`${getApiOrigin()}/api/v1/rique/transcribe`, {
    method: 'POST',
    headers: authHeaders(false),
    body: form,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || 'Falha na transcrição');
  }
  const body = await res.json();
  return (body.data?.transcricao as string) ?? '';
}
